import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import participantsPaneTheme from '../../../base/components/themes/participantsPaneTheme.json';
import Icon from '../../../base/icons/components/Icon';
import { IconExpandArrow, IconRaiseHandFill } from '../../../base/icons/svg';
import { withPixelLineHeight } from '../../../base/styles/functions.web';
import { countCascadeRaiseHands } from '../../../raisehand-moderation/functions';
import ParticipantActionEllipsis from './ParticipantActionEllipsis';
import { Trans } from 'react-i18next';
const useStyles = makeStyles()(theme => {
    return {
        raisedHandRoom: {
            display: "flex",
            flexDirection: "column",
            color: theme.palette.default00,
            ...withPixelLineHeight(theme.typography.bodyShortBold),
            position: 'relative',
            boxShadow: 'inset 0px -1px 0px rgba(255, 255, 255, 0.15)',
            minHeight: '40px',
            transitionDuration: '0.2s',
            [`@media(max-width: ${participantsPaneTheme.MD_BREAKPOINT})`]: {
                ...withPixelLineHeight(theme.typography.bodyShortBoldLarge),
                padding: `${theme.spacing(3)} ${participantsPaneTheme.panePadding}px`
            }
        },
        roomInfo: {
            display: "flex",
            padding: `${theme.spacing(1)} 8px ${theme.spacing(1)} 0`,
            '&:hover': {
                backgroundColor: theme.palette.bgParticipantHover,
                borderRadius: '6px',
            },
        },
        nameGroup: {
            display: 'flex',
            flexDirection: 'column',
            width: '60%'
        },
        name: {
            display: 'flex',
            flex: 1,
            // marginRight: theme.spacing(2),
            overflow: 'hidden',
            alignItems: "center",
            fontWeight: 500,
            maxWidth: '95%',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
        },
        forwarded: {
            fontStyle: 'italic',
            fontWeight: 400,
            maxWidth: '95%',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden'
        },
        ellipsis: {
            backgroundColor: "unset",
            padding: "0px !important",
            marginLeft: "6px",
            '&:hover': {
                backgroundColor: "unset",
            },
        },
        count: {
            padding: "0px 10px",
            backgroundColor: "#FF5C7F",
            borderRadius: "6px",
            color: "white",
            cursor: "pointer",
            height: "24px",
            margin: "auto",
            marginRight: "4px",
            '&:hover': {
                backgroundColor: "#FF5C7F80",
            },
        },
        userInfo: {
            display: 'flex',
            justifyContent: 'space-between',
            padding: "0px 55px 0 48px"
        }
    };
});
/**
 * Implements the MeetingParticipantItem component.
 *
 * @param {IProps} props - The props of the component.
 * @returns {ReactElement}
 */
function CascadeRaisedHandRoomItem({ _participantID, toggleMenu, lowerMenu, participantActionEllipsisLabel, renderAll = false, roomName, forwardRoomName, users, count, isDirect }) {
    const { classes } = useStyles();
    const onContextMenu = (e) => {
        toggleMenu(_participantID)(e);
    };
    const [openUsers, setOpenUsers] = useState(false);
    const refCountTime = useRef(null);
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        if (refCountTime.current !== null) {
            clearInterval(refCountTime.current);
        }
        if (openUsers) {
            setNow(Date.now()); // reset on open
            refCountTime.current = setInterval(() => setNow(Date.now()), 1000);
        }
        return () => {
            if (refCountTime.current !== null) {
                clearInterval(refCountTime.current);
            }
        };
    }, [openUsers]);
    function formatTimestamp(timestamp, isMillis = true) {
        // Convert timestamp to seconds if it's in milliseconds
        const totalSeconds = isMillis ? Math.floor(timestamp / 1000) : timestamp;
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        if (hours > 0) {
            return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        }
        else {
            return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        }
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: `list-item-container ${classes.raisedHandRoom}` },
            React.createElement("div", { className: classes.roomInfo },
                React.createElement(Icon, { onClick: () => setOpenUsers(cur => !cur), style: {
                        cursor: "pointer",
                        margin: '3px 6px 0 8px',
                        transform: `rotate(${openUsers ? '0' : '180'}deg)`,
                        transition: '0.2s',
                        width: '32px',
                        height: '32px'
                    }, src: IconExpandArrow, size: 32 }),
                isDirect ? (React.createElement("div", { className: classes.name }, roomName)) : (React.createElement("div", { className: classes.nameGroup },
                    React.createElement("div", { className: classes.name }, roomName),
                    React.createElement("div", { className: classes.forwarded },
                        React.createElement(Trans, { i18nKey: "roomCascade.raisedHand", values: { roomName: forwardRoomName } })))),
                React.createElement("div", { className: classes.count, onClick: () => setOpenUsers(cur => !cur) }, count),
                React.createElement(Icon, { className: 'jitsi-icon-pink', style: { margin: "auto" }, size: 16, src: IconRaiseHandFill }),
                isDirect ? (React.createElement(ParticipantActionEllipsis, { className: classes.ellipsis, accessibilityLabel: participantActionEllipsisLabel, onClick: onContextMenu, participantID: _participantID })) : (React.createElement("div", { style: { width: '24px', marginLeft: '6px' } }))),
            openUsers && (React.createElement("div", { id: `cascade-raised-room` }, users.map(user => (React.createElement("div", { key: user.name, id: `cascade-raised-room-${user.name}`, className: classes.userInfo },
                React.createElement("div", { className: classes.name }, user.name),
                React.createElement("div", null, formatTimestamp(now - user.timestamp))))))))));
}
/**
 * Maps (parts of) the redux state to the associated props for this component.
 *
 * @param {Object} state - The Redux state.
 * @param {Object} ownProps - The own props of the component.
 * @private
 * @returns {IProps}
 */
function _mapStateToProps(state, ownProps) {
    const { roomId, participantID, searchString } = ownProps;
    const count = countCascadeRaiseHands(state, roomId) || 0;
    return {
        _participantID: participantID ?? '',
        count,
    };
}
export default connect(_mapStateToProps)(CascadeRaisedHandRoomItem);
